/**
 * useHydraOrderbookWarmup — opens an SSE per Kalshi ticker so Hydra's
 * `subscribeOnDemand` flow (RestServer.ts /api/v1/stream/:ticker) starts
 * pulling that ticker's orderbook upstream. The actual data still arrives
 * through the existing WS firehose into marketStore — the SSE here exists
 * only to keep the upstream subscription alive while a tab is viewing the
 * game.
 *
 * Gated on `VITE_HYDRA_REST_URL`. When that env is unset the hook is a
 * no-op, which is the right behaviour until Hydra serves /api/* over HTTPS
 * (see docs/hydra-server-asks.md).
 *
 * Polymarket tickers are skipped — Hydra only on-demand-subscribes Kalshi.
 */

import { useEffect } from 'react';

const HYDRA_REST_URL = (import.meta.env.VITE_HYDRA_REST_URL as string | undefined)?.trim() || '';

export function useHydraOrderbookWarmup(tickers: ReadonlyArray<string | null | undefined>): void {
  const key = tickers.filter(Boolean).join('|');

  useEffect(() => {
    if (!HYDRA_REST_URL) return;

    const targets = tickers.filter(
      (t): t is string => typeof t === 'string' && t.length > 0 && !t.startsWith('PM-'),
    );
    if (targets.length === 0) return;

    const sources = targets.map((ticker) => {
      const url = `${HYDRA_REST_URL.replace(/\/$/, '')}/api/v1/stream/${encodeURIComponent(ticker)}`;
      const es = new EventSource(url, { withCredentials: false });
      es.onerror = () => {
        console.warn(`[HydraWarmup] SSE error for ${ticker} — Hydra may not be reachable; closing`);
        es.close();
      };
      return es;
    });

    return () => {
      for (const es of sources) es.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
